import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App.js';
import { watchConnection } from './lib/attempt-queue.js';
import { primeSpeechOnFirstTap } from './lib/speech.js';
import { markUpdateReady, watchForUpdates } from './lib/update.js';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A child on flaky iPad Wi-Fi should retry quietly rather than see an error.
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

// A new version waits until the app is between questions (lib/update.ts).
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh: () => markUpdateReady(() => void updateSW(true)),
  onRegisteredSW: (_url, registration) => {
    if (registration) watchForUpdates(registration);
  },
});

// iOS speaks only after a tap has spoken once; the PIN pad is that tap.
primeSpeechOnFirstTap();

// Flush any answers that were queued while the iPad was offline.
watchConnection();

const root = document.getElementById('root');
if (!root) throw new Error('no #root element');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
